import { DNSRecord } from './domainVerificationService';

export interface DNSLookupResult {
  success: boolean;
  records: string[];
  error?: string;
}

export interface DNSVerificationResult {
  recordName: string;
  recordType: string;
  expectedValue: string;
  actualValue?: string;
  verified: boolean;
  error?: string;
}

class DNSLookupService {
  private readonly DNS_SERVERS = [
    '8.8.8.8',      // Google DNS
    '1.1.1.1',      // Cloudflare DNS
    '208.67.222.222', // OpenDNS
  ];

  private readonly TIMEOUT = 10000; // 10 seconds
  private readonly MAX_RETRIES = 3;

  // Lookup TXT records for a domain
  async lookupTXT(domain: string): Promise<DNSLookupResult> {
    try {
      const response = await this.queryDNS(domain, 'TXT');
      return {
        success: true,
        records: response.answers || []
      };
    } catch (error: any) {
      console.error('TXT lookup error:', error);
      return {
        success: false,
        records: [],
        error: error.message
      };
    }
  }

  // Lookup CNAME records for a domain
  async lookupCNAME(domain: string): Promise<DNSLookupResult> {
    try {
      const response = await this.queryDNS(domain, 'CNAME');
      return {
        success: true,
        records: response.answers || []
      };
    } catch (error: any) {
      console.error('CNAME lookup error:', error);
      return {
        success: false,
        records: [],
        error: error.message
      };
    }
  }

  // Lookup MX records for a domain
  async lookupMX(domain: string): Promise<DNSLookupResult> {
    try {
      const response = await this.queryDNS(domain, 'MX');
      return {
        success: true,
        records: response.answers || []
      };
    } catch (error: any) {
      console.error('MX lookup error:', error);
      return {
        success: false,
        records: [],
        error: error.message
      };
    }
  }

  // Lookup A records for a domain
  async lookupA(domain: string): Promise<DNSLookupResult> {
    try {
      const response = await this.queryDNS(domain, 'A');
      return {
        success: true,
        records: response.answers || []
      };
    } catch (error: any) {
      console.error('A lookup error:', error);
      return {
        success: false,
        records: [],
        error: error.message
      };
    }
  }

  // Generic DNS query using DNS over HTTPS
  private async queryDNS(domain: string, recordType: string): Promise<any> {
    const dnsServer = this.DNS_SERVERS[0]; // Use Google DNS
    const url = `https://dns.google/resolve?name=${encodeURIComponent(domain)}&type=${recordType}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.TIMEOUT);

    try {
      const response = await fetch(url, {
        method: 'GET',
        signal: controller.signal,
        headers: {
          'Accept': 'application/dns-json',
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`DNS query failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (data.Status !== 0) {
        throw new Error(`DNS query failed with status: ${data.Status}`);
      }

      return {
        answers: data.Answer?.map((answer: any) => answer.data) || [],
        authority: data.Authority || [],
        additional: data.Additional || []
      };
    } catch (error: any) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  // Verify a specific DNS record
  async verifyRecord(recordName: string, recordType: 'TXT' | 'CNAME' | 'MX' | 'A', expectedValue: string): Promise<DNSVerificationResult> {
    const normalizeDomainValue = (value: string) =>
      value.trim().replace(/\.$/, '').toLowerCase();
    try {
      let lookupResult: DNSLookupResult;

      if (recordType === 'TXT') {
        lookupResult = await this.lookupTXT(recordName);
      } else if (recordType === 'CNAME') {
        lookupResult = await this.lookupCNAME(recordName);
      } else if (recordType === 'MX') {
        lookupResult = await this.lookupMX(recordName);
      } else if (recordType === 'A') {
        lookupResult = await this.lookupA(recordName);
      } else {
        throw new Error(`Unsupported record type: ${recordType}`);
      }

      if (!lookupResult.success) {
        return {
          recordName,
          recordType,
          expectedValue,
          verified: false,
          error: lookupResult.error || 'DNS lookup failed'
        };
      }

      // Check if the expected value exists in the records
      console.log(`Verifying ${recordType} record: ${recordName}`, { records: lookupResult.records, expectedValue });

      const found = lookupResult.records.some(record => {
        if (recordType === 'TXT') {
          // For TXT records, remove all quotes (they can appear in different formats)
          const cleanRecord = record.replace(/"/g, '').trim();
          console.log('Checking TXT record:', { cleanRecord, expectedValue });

          // Special handling for SPF records - check if Scubamail's include is present
          if (expectedValue.includes('v=spf1') && expectedValue.includes('include:spf.scubamail.io')) {
            // Just check if any TXT record contains the scubamail include (more lenient)
            const hasSPF = cleanRecord.includes('include:spf.scubamail.io');
            console.log('SPF check result:', hasSPF, 'record:', cleanRecord);
            return hasSPF;
          }

          // Special handling for DMARC records - just verify a DMARC record exists
          if (expectedValue.includes('v=DMARC1')) {
            const hasDMARC = cleanRecord.includes('v=DMARC1');
            console.log('DMARC check result:', hasDMARC);
            return hasDMARC;
          }

          // For other TXT records, do exact match
          return cleanRecord === expectedValue;
        } else if (recordType === 'CNAME') {
          // For CNAME records, direct comparison
          console.log('CNAME comparison:', { record: normalizeDomainValue(record), expected: normalizeDomainValue(expectedValue) });
          return normalizeDomainValue(record) === normalizeDomainValue(expectedValue);
        } else if (recordType === 'MX') {
          // For MX records, compare priority and exchange
          // Expected format: "10 mail.example.com" or just "mail.example.com"
          const cleanRecord = normalizeDomainValue(record.replace(/^\d+\s+/, '')); // Remove priority
          const cleanExpected = normalizeDomainValue(expectedValue.replace(/^\d+\s+/, '')); // Remove priority
          return cleanRecord === cleanExpected;
        } else if (recordType === 'A') {
          // For A records, direct IP comparison
          return record === expectedValue;
        }
        return false;
      });

      return {
        recordName,
        recordType,
        expectedValue,
        actualValue: lookupResult.records[0] || 'Not found',
        verified: found
      };
    } catch (error: any) {
      console.error('Record verification error:', error);
      return {
        recordName,
        recordType,
        expectedValue,
        verified: false,
        error: error.message
      };
    }
  }

  // Verify multiple DNS records with retry logic
  async verifyRecords(records: DNSRecord[]): Promise<DNSVerificationResult[]> {
    const results: DNSVerificationResult[] = [];

    for (const record of records) {
      if (record.status === 'verified') {
        // Skip already verified records
        results.push({
          recordName: record.name,
          recordType: record.type,
          expectedValue: record.value,
          verified: true
        });
        continue;
      }

      let lastError: string | undefined;

      // Retry logic for each record
      for (let attempt = 1; attempt <= this.MAX_RETRIES; attempt++) {
        try {
          const result = await this.verifyRecord(record.name, record.type, record.value);
          results.push(result);
          break; // Success, move to next record
        } catch (error: any) {
          lastError = error.message;
          if (attempt < this.MAX_RETRIES) {
            // Wait before retry (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
          }
        }
      }

      // If all retries failed, add error result
      if (results.length === 0 || results[results.length - 1].recordName !== record.name) {
        results.push({
          recordName: record.name,
          recordType: record.type,
          expectedValue: record.value,
          verified: false,
          error: lastError || 'All retry attempts failed'
        });
      }
    }

    return results;
  }

  // Check if a domain exists and is reachable
  async checkDomainExists(domain: string): Promise<boolean> {
    // Try multiple record types - many domains don't have A records at apex
    const typesToCheck = ['NS', 'MX', 'A', 'TXT'];

    for (const type of typesToCheck) {
      try {
        const result = await this.queryDNS(domain, type);
        if (result.answers && result.answers.length > 0) {
          console.log(`Domain ${domain} verified via ${type} record`);
          return true;
        }
      } catch (error) {
        // Continue to next type
      }
    }

    console.error('Domain existence check failed for:', domain);
    return false;
  }

  // Get all DNS records for a domain (for debugging)
  async getAllRecords(domain: string): Promise<{
    A: string[];
    AAAA: string[];
    CNAME: string[];
    MX: string[];
    TXT: string[];
    NS: string[];
  }> {
    const recordTypes = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS'];
    const results: any = {};

    for (const type of recordTypes) {
      try {
        const response = await this.queryDNS(domain, type);
        results[type] = response.answers?.map((answer: any) => answer.data) || [];
      } catch (error) {
        console.error(`Failed to get ${type} records:`, error);
        results[type] = [];
      }
    }

    return results;
  }

  // Validate domain format
  isValidDomain(domain: string): boolean {
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.([a-zA-Z]{2,}|[a-zA-Z]{2,}\.[a-zA-Z]{2,})$/;
    return domainRegex.test(domain);
  }

  // Extract domain from record name
  extractDomainFromRecord(recordName: string): string {
    const parts = recordName.split('.');
    if (parts.length >= 2) {
      return parts.slice(-2).join('.');
    }
    return recordName;
  }
}

export const dnsLookupService = new DNSLookupService();
